import { Popover, f7ready } from 'framework7-react';
import {
  ComponentType,
  ErrorInfo,
  FC,
  MouseEventHandler,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { Popover as PopoverType } from 'framework7/types';

import { ErrorBoundary } from '../ErrorBoundary';
import './PopoverWrapper.scss';

export const usePopoverRef = () => {
  const popoverRef = useRef<PopoverType.Popover>(null);

  return popoverRef;
};

export type PopoverContentComponent = ComponentType<{ onClose: () => void }>;

interface PopoverWrapperProps extends PropsWithChildren {
  className?: string;
  PopoverContent: PopoverContentComponent;
  isVisible?: boolean;
  onClose?: () => void;
}

const PopoverWrappeUnwrapped: FC<PopoverWrapperProps> = ({
  className = '',
  PopoverContent,
  children,
  isVisible: controlledIsVisible,
  onClose,
}) => {
  const [isMounted, setIsMounted] = useState(controlledIsVisible);
  const popoverRef = usePopoverRef();
  const divRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    setIsMounted(true);

    return () => {
      setIsMounted(false);
    };
  }, []);

  useLayoutEffect(() => {
    if (isMounted) {
      f7ready((f7) => {
        try {
          if (divRef.current) {
            popoverRef.current = f7.popover.create({
              el: divRef.current,
              on: {
                closed: () => {
                  onClose?.();
                },
              },
            });
          }
        } catch (error) {
          console.error('Error creating popover', error);
        }
      });
    }

    return () => {
      try {
        popoverRef.current?.close?.();
        onClose?.();
        popoverRef.current?.destroy?.();
      } catch (error) {
        console.error('Error destroying popover', error);
      }
    };
  }, [isMounted]);

  const wrapperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (controlledIsVisible && wrapperRef.current) {
      popoverRef.current?.open(wrapperRef.current);
    } else {
      popoverRef.current?.close();
    }
  }, [controlledIsVisible]);

  const handleClick: MouseEventHandler = (e) => {
    e.stopPropagation();
    e.preventDefault();
    popoverRef.current?.open(e.target as HTMLElement);
  };

  const handleClose = () => {
    popoverRef.current?.close();
    onClose?.();
  };

  const popover = useMemo(
    () =>
      document.getElementById('framework7-root') &&
      createPortal(
        <div ref={divRef} className="popover">
          <div className="popover-arrow"></div>
          <div className="popover-inner">
            <PopoverContent onClose={handleClose} />
          </div>
        </div>,
        document.getElementById('framework7-root'),
      ),
    [],
  );

  return (
    <>
      <div ref={wrapperRef} className={`popover-wrapper ${className}`} onClick={handleClick}>
        <div className="popover-wrapper-inner" />
        {children}
      </div>
      {isMounted && popover}
    </>
  );
};

export const PopoverWrapper = (props: PropsWithChildren<PopoverWrapperProps>) => {
  const handlePopoverError = useCallback((error: Error, errorInfo: ErrorInfo) => {
    console.error('Popover Error', { error, errorInfo });
  }, []);

  return (
    <ErrorBoundary hideOnError onError={handlePopoverError}>
      <PopoverWrappeUnwrapped {...props} />
    </ErrorBoundary>
  );
};
